/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ApiService, AuthStoreService } from '../../../../core/services';
import { BackupApiEnum } from '../enums/api';
import { BackupModel } from '../models';
import { ErrorResponseModel } from '../../../../core/models';
import { Injectable } from '@angular/core';
import { ModulesEnum } from '../../../../core/enums';
import { Observable, Subject } from 'rxjs';

@Injectable()
export class BackupApiService {
    /**
     * Emit when get backups is called.
     */
    public getBackupsResponse: Subject<any> = new Subject<any>();

    constructor(
        private api: ApiService,
        private authStoreService: AuthStoreService
    ) {
    }

    /**
     * Get retrieves all adc backups for a namespace.
     * @param namespaceId
     * @param options
     */
    public getBackupsByNamespaceId(namespaceId: number, options?: any): Observable<BackupModel[]> {
        return new Observable((observer): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.get(
                BackupApiEnum.GET_BACKUPS_ALL(namespaceId.toString()),
                moduleAuth
            ).subscribe({
                next: (data: BackupModel[]): void => {
                    this.getBackupsResponse.next(data);
                    observer.next(data);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * TODO this is currently being bypassed as we can't pass the options in for returning a blob
     * Get backup by ID for download.
     * @param namespaceId
     * @param backupId
     * @param options
     */
    public getBackupById(namespaceId: number, backupId: string, options?: any): Observable<any> {
        return new Observable((observer): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);
            this.api.get(
                BackupApiEnum.GET_BACKUP(namespaceId.toString(), backupId),
                moduleAuth
            ).subscribe({
                next: (data: any): void => {
                    observer.next(data);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Backup an adc.
     * @param namespaceId
     * @param adcId
     * @param options
     */
    public backupAdc(namespaceId: number, adcId: number, options?: any): Observable<any> {
        return new Observable((observer): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.post(
                BackupApiEnum.POST_BACKUP(namespaceId.toString(), adcId.toString()),
                adcId,
                moduleAuth
            ).subscribe({
                next: (data: any): void => {
                    observer.next(data);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Delete backup.
     * @param namespaceId
     * @param backupId
     * @param options
     */
    public deleteBackup(namespaceId: number, backupId: string, options?: any): Observable<any> {
        return new Observable((observer): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.api.delete(
                BackupApiEnum.DELETE_BACKUP(namespaceId.toString(), backupId),
                moduleAuth
            ).subscribe({
                next: (data: any): void => {
                    observer.next(data);
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }
}
